import React, { useState } from 'react'
import dayjs from 'dayjs'
import { Box, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { PickersDay } from '@mui/x-date-pickers/PickersDay'
import { C } from './tokens'
import { dayLong } from './format'

/**
 * A two-click range picker: the first click sets the start, the second sets
 * the end and applies immediately — no separate Apply button. Built on the
 * free DateCalendar; MUI's DateRangePicker is a paid (Pro) component.
 */
function RangeDay(props) {
  const { day, start, end, hover, outsideCurrentMonth, ...other } = props
  const d = day.format('YYYY-MM-DD')
  const hi = end || hover
  const lo = start && hi ? (start < hi ? start : hi) : start
  const up = start && hi ? (start < hi ? hi : start) : start
  const inRange = lo && up && d > lo && d < up
  const isEdge = d === lo || d === up
  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={!outsideCurrentMonth && isEdge}
      sx={{
        borderRadius: isEdge ? '50%' : 0,
        ...(inRange && !outsideCurrentMonth ? { bgcolor: C.brandSoft, color: C.text, '&:hover': { bgcolor: C.brandFill } } : {}),
        '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': { bgcolor: C.brandText, color: '#fff' }
      }}
    />
  )
}

export default function RangePicker({ from, to, max, onChange }) {
  const [start, setStart] = useState(null)
  const [hover, setHover] = useState(null)

  const pick = value => {
    const d = value.format('YYYY-MM-DD')
    if (!start) {
      setStart(d)
      return
    }
    const [a, b] = start <= d ? [start, d] : [d, start]
    setStart(null)
    setHover(null)
    onChange(a, b)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 1 }}>
        <Typography sx={{ fontSize: 12, color: C.textMuted, px: 2, pt: 1 }} aria-live="polite">
          {start ? `From ${dayLong(start)} — now pick the end date` : `Showing ${dayLong(from)} – ${dayLong(to)}. Pick a start date.`}
        </Typography>
        <DateCalendar
          value={dayjs(start || to)}
          maxDate={dayjs(max)}
          views={['day']}
          onChange={(value, state) => { if (value && state !== 'partial') pick(value) }}
          slots={{ day: RangeDay }}
          slotProps={{
            day: ownerState => ({
              start: start || from,
              end: start ? null : to,
              hover: start ? hover : null,
              onPointerEnter: () => start && setHover(ownerState.day.format('YYYY-MM-DD'))
            })
          }}
        />
      </Box>
    </LocalizationProvider>
  )
}
